import React, { useEffect } from 'react';
import { Route, Switch, useRouteMatch } from 'react-router-dom';
import { StepController } from '../../shared/steps/StepController';
import { useRecordInteractionMutation } from '../generated.graphql';
import { usePortalAnalytics } from '../hooks/analytics';
import { AboutMe } from '../steps/aboutme/AboutMe';
import { AdditionalInfo } from '../steps/additional/AdditionalInfo';
import { Address } from '../steps/address/Address';
import { Agreement } from '../steps/agreement/Agreement';
import { AlternativeContact } from '../steps/altcontact/AlternativeContact';
import { BankStatements } from '../steps/bankstatements/BankStatements';
import { Cart } from '../steps/cart/Cart';
import { SubmittingApplication } from '../steps/finished/Finished';
import { ImportantInfo } from '../steps/importantinfo/ImportantInfo';
import { Income } from '../steps/income/Income';
import { Payment } from '../steps/payment/Payment';
import { Signature } from '../steps/signature/Signature';
import { Summary } from '../steps/summary/Summary';
import { Welcome } from '../steps/welcome/Welcome';
import { AppContextData } from './AppContext';
import { PageNotFound } from './PageNotFound';
import { isSlug, slugs, useRentalSteps } from './steps';


interface Props extends AppContextData {
  
}

export const AppContent = (props: Props) => {
  const match = useRouteMatch<{slug: string}>(props.buildLink(':slug'))
  const matchedSlug = match?.params.slug || '';
  const steps = useRentalSteps({slug: isSlug(matchedSlug) ? matchedSlug : slugs.cart});
  const { pageView, updateCheckoutProgress } = usePortalAnalytics(props.portal);

  // Make a Record Interaction mutation every time the path changes
  /* eslint-disable react-hooks/exhaustive-deps */
  const [, executeRecordInteraction] = useRecordInteractionMutation();
  useEffect(
    // On url change, send page view and record an interaction
    () => {
      pageView(undefined, steps.this?.slug);

      // Only record interactions for valid steps that the user has reached
      if (isSlug(matchedSlug) && steps.this?.enabled) {
        executeRecordInteraction({
          input: {
            portalSlug: props.portalSlug,
            stepSlug: steps.this.slug,
          }
        });
      }
    }, 
    // Only call the effect when the url changes (matchedSlug) and the current step info (steps.this) stages
    [steps.this?.slug, steps.this?.enabled, matchedSlug]
  )
  /* eslint-enable react-hooks/exhaustive-deps */

  if (!steps.this) 
    return <PageNotFound portal={props.portal}/>;
  
  return (
    <StepController
      steps={{...steps, this: steps.this}}  // Being explicit since this cannot be null
      onStepForward={(curStep, nextStep) => updateCheckoutProgress(props.application, curStep.slug, nextStep.slug)} 
      {...props}
    >
      {stepProps => (

        <Switch>
          <Route path={props.buildLink(slugs.welcome)}><Welcome {...{...props, ...stepProps}}/></Route>
          <Route path={props.buildLink(slugs.cart)}><Cart  {...{...props, ...stepProps}}/></Route>
          <Route path={props.buildLink(slugs.importantInfo)}><ImportantInfo  {...{...props, ...stepProps}} /></Route>
          <Route path={props.buildLink(slugs.aboutMe)}><AboutMe  {...{...props, ...stepProps}}/></Route>
          <Route path={props.buildLink(slugs.altContact)}><AlternativeContact {...{...props, ...stepProps}}/></Route>
          <Route path={props.buildLink(slugs.address)}><Address {...{...props, ...stepProps}}/></Route>
          <Route path={props.buildLink(slugs.income)}><Income {...{...props, ...stepProps}}/></Route>
          <Route path={props.buildLink(slugs.additionalInfo)}><AdditionalInfo {...{...props, ...stepProps}}/></Route>
          <Route path={props.buildLink(slugs.bankStatements)}><BankStatements {...{...props, ...stepProps}}/></Route>
          <Route path={props.buildLink(slugs.payment)}><Payment {...{...props, ...stepProps}}/></Route>
          {/* <Route path={props.buildLink(slugs.paid)}><Paid {...{...props, ...stepProps}}/></Route> */}
          <Route path={props.buildLink(slugs.agreement)}><Agreement {...{...props, ...stepProps}}/></Route>
          <Route path={props.buildLink(slugs.summary)}><Summary {...{...props, ...stepProps}}/></Route>
          <Route path={props.buildLink(slugs.signature)}><Signature {...{...props, ...stepProps}}/></Route>
          <Route path={props.buildLink(slugs.finished)}><SubmittingApplication /></Route>
          <Route>
            <PageNotFound portal={props.portal}/>
          </Route>
        </Switch>
      )}
    </StepController>
  )
};